import { useMemo } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';

// assets
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import ShoppingCartOutlined from '@ant-design/icons/ShoppingCartOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

// project imports
import useAuth from 'hooks/useAuth';
import useRules from 'hooks/useRules';
import { handlerDrawerOpen } from 'api/menu';

type BottomNavItem = {
  id: string;
  label: string;
  icon: React.ReactElement;
  url?: string;
  onClick?: () => void;
};

/**
 * Navegação inferior fixa, só em telas de celular (down('sm')). O menu lateral completo
 * (Administração, etc.) continua acessível pelo hambúrguer do Header — isso aqui é só um
 * atalho rápido para as telas mais usadas no dia a dia, no estilo dos apps mobile nativos.
 */
export default function BottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useRules();
  const userRules = user?.rules ?? [];

  const items = useMemo<BottomNavItem[]>(() => {
    const list: BottomNavItem[] = [];
    if (isAdmin || userRules.includes('products.read')) {
      list.push({ id: 'products', label: 'Produtos', icon: <GiftOutlined />, url: '/products' });
    }
    if (isAdmin || userRules.includes('orders.read')) {
      list.push({ id: 'orders', label: 'Pedidos', icon: <ShoppingCartOutlined />, url: '/orders' });
    }
    list.push({
      id: 'menu',
      label: 'Menu',
      icon: <SettingOutlined />,
      onClick: () => handlerDrawerOpen(true)
    });
    list.push({ id: 'account', label: 'Perfil', icon: <UserOutlined />, url: '/account' });
    return list;
  }, [isAdmin, userRules]);

  const currentValue = items.find((item) => item.url && matchPath({ path: item.url, end: false }, pathname))?.id ?? false;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        borderTopLeftRadius: (theme) => theme.shape.borderRadius,
        borderTopRightRadius: (theme) => theme.shape.borderRadius,
        overflow: 'hidden'
      }}
    >
      <BottomNavigation
        showLabels
        value={currentValue}
        onChange={(_e, newValue) => {
          const item = items.find((i) => i.id === newValue);
          if (!item) return;
          if (item.onClick) item.onClick();
          else if (item.url) navigate(item.url);
        }}
        sx={{ height: 64 }}
      >
        {items.map((item) => (
          <BottomNavigationAction key={item.id} value={item.id} label={item.label} icon={item.icon} />
        ))}
      </BottomNavigation>
    </Paper>
  );
}
